import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { mergePublicTheme, publicThemeVars } from '../publicTheme';
import PublicNavbar from './PublicNavbar';
import Carousel from './Carousel';
import { ReviewModal, CommentsModal, StarsDisplay, ratingSummary } from './Reviews';
import { usePublicSocket } from '../hooks/usePublicSocket';

const fmt = (v) => `$${parseFloat(v || 0).toLocaleString('es-CO')}`;
// Detecta si una URL de la galería corresponde a un video (para reproducirlo
// en vez de mostrarlo como imagen).
const isVideoUrl = (url) => /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(url || '');
const pad = n => String(n).padStart(2, '0');
// Celular de una empresa que manda plantillas (store.strict_phone): solo números,
// sin +, espacios ni guiones. El servidor le pone el indicativo del país si no lo trae.
const onlyDigits = (v) => String(v || '').replace(/\D/g, '').slice(0, 15);
const toDateStr = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const todayStr = () => { const d = new Date(); return toDateStr(d.getFullYear(), d.getMonth(), d.getDate()); };
const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const DAY_HEADERS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const DAY_NAMES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
// "2026-09-10" -> "Jueves 10 de septiembre". Se construye la fecha con los números
// sueltos para que no la desplace la zona horaria del celular del cliente.
const prettyDate = (iso) => {
  const [y, m, d] = String(iso || '').split('-').map(Number);
  if (!y || !m || !d) return iso;
  const dayName = DAY_NAMES[new Date(y, m - 1, d).getDay()];
  return `${dayName} ${d} de ${MONTH_NAMES[m - 1].toLowerCase()}`;
};

function PublicCita() {
  const { slug } = useParams();
  const [store, setStore] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [view, setView] = useState('catalog');        // 'catalog' | 'schedule'

  // Filtros del catálogo. `applied` son los filtros que realmente se aplican y
  // solo cambian al presionar "Buscar" (no en cada tecla).
  const [search, setSearch] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [applied, setApplied] = useState({ search: '', minPrice: '', maxPrice: '' });
  const applyFilters = () => setApplied({ search, minPrice, maxPrice });
  const clearFilters = () => { setSearch(''); setMinPrice(''); setMaxPrice(''); setApplied({ search: '', minPrice: '', maxPrice: '' }); };

  // Modal de detalle del servicio
  const [modalService, setModalService] = useState(null);
  const [imgIndex, setImgIndex] = useState(0);

  // Agendamiento
  const [chosenService, setChosenService] = useState(null);
  const [staff, setStaff] = useState([]);          // usuarios con quienes agendar
  const [chosenUserId, setChosenUserId] = useState(''); // '' = ninguno elegido
  const [date, setDate] = useState('');
  const now = new Date();
  const [calYear, setCalYear] = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth());
  const [slotsData, setSlotsData] = useState(null);   // { dayEnabled, isHoliday, slots }
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [time, setTime] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [done, setDone] = useState(null);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [commentsTarget, setCommentsTarget] = useState(null);
  const [reviewRefresh, setReviewRefresh] = useState(0);
  const [liveRefresh, setLiveRefresh] = useState(0);

  // Cancelación por parte del cliente: consulta sus citas pendientes con el celular
  // y las cancela. El servidor solo cambia el estado, nunca borra la cita.
  const [showCancel, setShowCancel] = useState(false);
  const [cancelPhone, setCancelPhone] = useState('');
  const [cancelList, setCancelList] = useState(null);   // null = todavía no ha buscado
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [confirmId, setConfirmId] = useState(null);     // cita esperando confirmación
  const [cancelledMsg, setCancelledMsg] = useState('');

  // Consultar cita: para quien olvidó cuándo se la asignaron. Trae las 3 últimas
  // citas de ese celular, aunque ya hayan pasado o estén canceladas.
  const [showLookup, setShowLookup] = useState(false);
  const [lookupPhone, setLookupPhone] = useState('');
  const [lookupResult, setLookupResult] = useState(null); // null = todavía no ha buscado
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupError, setLookupError] = useState('');

  // Tiempo real: si el admin cambia un servicio o la configuración/carrusel de la
  // empresa, recargamos los servicios sin que el cliente tenga que recargar.
  usePublicSocket(slug, ['service_changed', 'company_changed'], () => setLiveRefresh(n => n + 1));

  useEffect(() => {
    fetch(`/api/cita/${encodeURIComponent(slug)}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(setStore)
      .catch(() => setNotFound(true));
  }, [slug, reviewRefresh, liveRefresh]);

  // Con calendario por usuario: cargar los usuarios con quienes se puede agendar
  // este servicio. Si solo hay uno, se elige automáticamente.
  useEffect(() => {
    setStaff([]); setChosenUserId('');
    if (!store?.per_user_calendar || !chosenService) return;
    const params = new URLSearchParams({ service_id: chosenService.id });
    fetch(`/api/cita/${encodeURIComponent(slug)}/staff?${params.toString()}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => {
        const list = Array.isArray(d.staff) ? d.staff : [];
        setStaff(list);
        if (list.length === 1) setChosenUserId(String(list[0].id));
      })
      .catch(() => setStaff([]));
  }, [slug, chosenService, store?.per_user_calendar, liveRefresh]);

  // Recargar horarios al cambiar servicio elegido, fecha o usuario elegido
  useEffect(() => {
    setTime('');
    setSlotsData(null);
    if (!chosenService || !date) return;
    if (store?.per_user_calendar && !chosenUserId) return; // falta elegir con quién
    setLoadingSlots(true);
    const params = new URLSearchParams({ service_id: chosenService.id, date });
    if (store?.per_user_calendar && chosenUserId) params.set('user_id', chosenUserId);
    fetch(`/api/cita/${encodeURIComponent(slug)}/slots?${params.toString()}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(setSlotsData)
      .catch(() => setSlotsData({ dayEnabled: true, isHoliday: false, slots: [] }))
      .finally(() => setLoadingSlots(false));
  }, [slug, chosenService, date, chosenUserId, store?.per_user_calendar]);

  const services = store?.services || [];
  // La empresa puede ocultar el buscador; entonces tampoco se aplica ningún filtro
  // que hubiera quedado puesto antes de desactivarlo.
  const searchOn = store?.buscador_activa !== false;
  const filtered = useMemo(() => {
    if (!searchOn) return services;
    const q = applied.search.trim().toLowerCase();
    const min = applied.minPrice !== '' ? Number(applied.minPrice) : null;
    const max = applied.maxPrice !== '' ? Number(applied.maxPrice) : null;
    return services.filter(s => {
      if (q && !s.name.toLowerCase().includes(q)) return false;
      if (min != null && (s.price == null || s.price < min)) return false;
      if (max != null && (s.price == null || s.price > max)) return false;
      return true;
    });
  }, [services, applied, searchOn]);

  const openModal = (s) => { setModalService(s); setImgIndex(0); };
  const modalImages = modalService
    ? [modalService.image_link, ...(modalService.additional_image_links || [])].filter(Boolean)
    : [];

  const startScheduling = (s) => {
    setChosenService(s);
    setModalService(null);
    setDate(''); setTime(''); setName(''); setPhone(''); setErrorMsg('');
    setView('schedule');
  };

  const openCancel = () => {
    setShowCancel(true);
    setCancelPhone(''); setCancelList(null); setCancelError('');
    setConfirmId(null); setCancelledMsg('');
  };

  const searchMyAppointments = async () => {
    setCancelError(''); setCancelledMsg(''); setConfirmId(null);
    setCancelBusy(true);
    try {
      const r = await fetch(`/api/cita/${encodeURIComponent(slug)}/mis-citas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: cancelPhone.trim() }),
      });
      const d = await r.json();
      if (!r.ok) {
        setCancelError(d.message || 'No se pudo consultar.');
        setCancelList(null);
      } else {
        setCancelList(d.appointments || []);
      }
    } catch {
      setCancelError('No se pudo consultar. Revisa tu conexión.');
      setCancelList(null);
    }
    setCancelBusy(false);
  };

  const openLookup = () => {
    setShowLookup(true);
    setLookupPhone(''); setLookupResult(null); setLookupError('');
  };

  const searchLastAppointments = async () => {
    setLookupError(''); setLookupBusy(true);
    try {
      const r = await fetch(`/api/cita/${encodeURIComponent(slug)}/ultimas-citas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: lookupPhone.trim() }),
      });
      const d = await r.json();
      if (!r.ok) {
        setLookupError(d.message || 'No se pudo consultar.');
        setLookupResult(null);
      } else {
        setLookupResult(d.appointments || []);
      }
    } catch {
      setLookupError('No se pudo consultar. Revisa tu conexión.');
      setLookupResult(null);
    }
    setLookupBusy(false);
  };

  const confirmCancel = async (id) => {
    setCancelError(''); setCancelBusy(true);
    try {
      const r = await fetch(`/api/cita/${encodeURIComponent(slug)}/cancelar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: cancelPhone.trim(), appointment_id: id }),
      });
      const d = await r.json();
      if (!r.ok) {
        setCancelError(d.message || 'No se pudo cancelar.');
      } else {
        setCancelledMsg('Cita cancelada');
        setCancelList(list => (list || []).filter(a => a.id !== id));
      }
    } catch {
      setCancelError('No se pudo cancelar. Revisa tu conexión.');
    }
    setConfirmId(null);
    setCancelBusy(false);
  };

  // chosenMethod solo importa cuando la empresa acepta ambos ('both'); en los demás
  // casos el servidor ignora el valor y usa el método configurado por la empresa.
  const submit = async (chosenMethod) => {
    setErrorMsg('');
    if (!chosenService || !date || !time || !name.trim() || !phone.trim()) {
      setErrorMsg('Completa fecha, hora, nombre y teléfono.');
      return;
    }
    if (store.per_user_calendar && !chosenUserId) {
      setErrorMsg('Elige con quién quieres agendar.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`/api/cita/${encodeURIComponent(slug)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ service_id: chosenService.id, date, time, name: name.trim(), phone: phone.trim(), payment_method: chosenMethod, user_id: chosenUserId || undefined }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        if (d.payment_method === 'online') {
          // La cita se agenda al aprobarse el pago; redirigir a Wompi.
          window.location.href = d.payment_url;
          return;
        }
        setDone({ ...d, name: name.trim() });
      } else {
        setErrorMsg(d.message || 'No se pudo agendar la cita.');
        if (res.status === 409 && chosenService && date) {
          const params = new URLSearchParams({ service_id: chosenService.id, date });
          if (store.per_user_calendar && chosenUserId) params.set('user_id', chosenUserId);
          fetch(`/api/cita/${encodeURIComponent(slug)}/slots?${params.toString()}`)
            .then(r => r.ok ? r.json() : null).then(sd => { if (sd) { setSlotsData(sd); setTime(''); } });
        }
      }
    } catch {
      setErrorMsg('Error de conexión. Intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const prevMonth = () => {
    if (calMonth === 0) { setCalYear(y => y - 1); setCalMonth(11); } else setCalMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalYear(y => y + 1); setCalMonth(0); } else setCalMonth(m => m + 1);
  };

  // Almanaque para elegir el día (estilo del administrador)
  const renderCalendar = () => {
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
    const firstDow = (new Date(calYear, calMonth, 1).getDay() + 6) % 7; // 0 = Lun
    const tStr = todayStr();
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    const navBtn = { background: 'none', border: `1px solid ${theme.colors.border}`, color: theme.colors.text, borderRadius: '7px', padding: '5px 14px', cursor: 'pointer', fontSize: '1rem', lineHeight: 1 };

    return (
      <div style={{ backgroundColor: theme.colors.background, border: `1px solid ${theme.colors.border}`, borderRadius: '10px', padding: '0.9rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.8rem' }}>
          <button type="button" onClick={prevMonth} style={navBtn}>←</button>
          <span style={{ color: theme.colors.primary, fontWeight: 700, fontSize: '1rem' }}>{MONTH_NAMES[calMonth]} {calYear}</span>
          <button type="button" onClick={nextMonth} style={navBtn}>→</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '4px' }}>
          {DAY_HEADERS.map(h => (
            <div key={h} style={{ textAlign: 'center', color: theme.colors.textMuted, fontSize: '0.7rem', fontWeight: 600 }}>{h}</div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
          {cells.map((d, i) => {
            if (d === null) return <div key={`e${i}`} />;
            const ds = toDateStr(calYear, calMonth, d);
            const isPast = ds < tStr;
            const isToday = ds === tStr;
            const selected = ds === date;
            return (
              <button
                key={d} type="button" disabled={isPast}
                onClick={() => setDate(ds)}
                style={{
                  padding: '8px 0', borderRadius: '7px', fontSize: '0.85rem',
                  border: `1px solid ${selected ? theme.colors.primary : (isToday ? theme.colors.primary : theme.colors.border + '44')}`,
                  backgroundColor: selected ? theme.colors.primary : 'transparent',
                  color: selected ? theme.colors.background : (isPast ? theme.colors.textMuted : theme.colors.text),
                  cursor: isPast ? 'not-allowed' : 'pointer',
                  opacity: isPast ? 0.35 : 1,
                  fontWeight: (selected || isToday) ? 700 : 400,
                }}
              >{d}</button>
            );
          })}
        </div>
      </div>
    );
  };

  const theme = mergePublicTheme(store?.theme);
  const themeWrap = { minHeight: '100vh', backgroundColor: theme.colors.background, color: theme.colors.text, ...publicThemeVars(theme.colors) };

  if (notFound) return <main style={center}>Agendamiento no disponible.</main>;
  if (!store) return <main style={center}>Cargando...</main>;

  // Confirmación
  if (done) {
    return (
      <div style={themeWrap}>
      <main style={{ maxWidth: '480px', margin: '0 auto', padding: '3rem 1.5rem', textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
        <h1 style={{ color: theme.colors.primary, fontSize: '1.4rem' }}>¡Cita agendada!</h1>
        <p style={{ color: theme.colors.text, lineHeight: 1.7, marginTop: '1rem' }}>
          {done.name}, tu cita para <strong>{done.service}</strong> quedó reservada el{' '}
          <strong>{done.date}</strong> a las <strong>{done.time}</strong>.
        </p>
        <p style={{ color: theme.colors.textMuted, fontSize: '0.9rem', marginTop: '0.5rem' }}>
          {store.name} te contactará si es necesario.
        </p>
        {done.id != null && (
          <div style={{ backgroundColor: theme.colors.surface, border: `2px dashed ${theme.colors.primary}`, borderRadius: '12px', padding: '1.1rem', marginTop: '1.5rem' }}>
            <p style={{ color: theme.colors.textMuted, fontSize: '0.85rem', margin: '0 0 0.3rem' }}>📌 El número de tu cita es</p>
            <div style={{ color: theme.colors.primary, fontSize: '2.2rem', fontWeight: 800, fontFamily: 'monospace', letterSpacing: '1px' }}>#{done.id}</div>
            <p style={{ color: theme.colors.text, fontSize: '0.88rem', lineHeight: 1.55, margin: '0.6rem 0 0' }}>
              ⭐ ¡Guarda este número! Con él podrás <strong>calificar y comentar</strong> el servicio que adquiriste. No lo pierdas.
            </p>
          </div>
        )}
        <button onClick={() => { setDone(null); setChosenService(null); setView('catalog'); }} style={{ ...primaryBtn, marginTop: '1.5rem' }}>
          Agendar otra cita
        </button>
      </main>
      </div>
    );
  }

  return (
    <div style={themeWrap}>
    <PublicNavbar slug={slug} store={store} current="cita" subtitle="Agenda tu cita" maxWidth="1000px" />
    <main style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 1.5rem 1.5rem' }}>
      {/* ─── CATÁLOGO ─── */}
      {view === 'catalog' && (
        <>
          {services.length === 0 ? (
            <p style={{ color: theme.colors.textMuted, textAlign: 'center', padding: '2rem' }}>
              Esta empresa aún no tiene servicios disponibles para agendar.
            </p>
          ) : (
            <>
              {/* Barra de filtros (la empresa puede ocultarla) */}
              {searchOn && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.7rem', alignItems: 'center', marginBottom: '1.5rem', backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.border}`, borderRadius: '12px', padding: '0.9rem 1rem' }}>
                <div style={{ position: 'relative', flex: '1 1 240px', minWidth: '200px' }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: theme.colors.textMuted, pointerEvents: 'none' }}>🔍</span>
                  <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') applyFilters(); }} placeholder="Buscar servicio..." style={{ ...input, paddingLeft: '34px' }} />
                </div>
                <input type="number" onWheel={e => e.currentTarget.blur()} min="0" value={minPrice} onChange={e => setMinPrice(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') applyFilters(); }} placeholder="Precio mín." style={{ ...input, flex: '0 1 130px' }} />
                <input type="number" onWheel={e => e.currentTarget.blur()} min="0" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') applyFilters(); }} placeholder="Precio máx." style={{ ...input, flex: '0 1 130px' }} />
                <button onClick={applyFilters} style={{ ...primaryBtn, flex: '0 0 auto', padding: '9px 20px' }}>🔍 Buscar</button>
                {(applied.search || applied.minPrice || applied.maxPrice) && (
                  <button onClick={clearFilters} style={{ ...secondaryBtn, flex: '0 0 auto' }}>Limpiar</button>
                )}
              </div>
              )}

              {/* Carrusel de imágenes */}
              {store.carousel?.images?.length > 0 && (
                <Carousel images={store.carousel.images} interval={store.carousel.interval} />
              )}

              {/* Consultar / cancelar cita: el cliente busca sus citas por celular */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '1.2rem' }}>
                <button onClick={openLookup} style={{ ...primaryBtn, padding: '10px 20px', fontSize: '0.92rem' }}>Consultar cita</button>
                <button onClick={openCancel} style={cancelCitaBtn}>Cancelar cita</button>
              </div>

              {filtered.length === 0 ? (
                <p style={{ color: theme.colors.textMuted, textAlign: 'center', padding: '3rem' }}>No se encontraron servicios con esos filtros.</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '1rem' }}>
                  {filtered.map(s => { const { avg, count } = ratingSummary(s.comments); return (
                    <div key={s.id} style={{ backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.border}`, borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                      <div onClick={() => openModal(s)} style={{ cursor: 'pointer' }}>
                        {s.image_link
                          ? <img src={s.image_link} alt={s.name} style={{ width: '100%', height: '160px', objectFit: 'cover' }} />
                          : <div style={{ width: '100%', height: '160px', backgroundColor: theme.colors.background, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.colors.textMuted, fontSize: '0.8rem' }}>Sin imagen</div>}
                        <div style={{ padding: '0.8rem 0.8rem 0.4rem' }}>
                          <p style={{ color: theme.colors.text, fontSize: '0.92rem', fontWeight: 600, margin: '0 0 6px' }}>{s.name}</p>
                          <p style={{ color: theme.colors.primary, fontSize: '0.95rem', fontWeight: 700, margin: '0 0 2px' }}>{s.price != null ? fmt(s.price) : 'Consultar'}</p>
                          <p style={{ color: theme.colors.textMuted, fontSize: '0.8rem', margin: 0 }}>⏱ {s.time} min</p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.4rem', padding: '0 0.7rem 0.7rem', marginTop: 'auto', flexWrap: 'wrap' }}>
                        {/* Mismo efecto que clic en la tarjeta: a los clientes no les resultaba obvio que la tarjeta era clicable */}
                        <button onClick={() => openModal(s)} style={{ ...primaryBtn, width: '100%', padding: '8px 10px', fontSize: '0.85rem' }}>📅 Agendar</button>
                        <button onClick={e => { e.stopPropagation(); setReviewTarget(s); }} style={reviewBtn}>★ Calificar</button>
                        <button onClick={e => { e.stopPropagation(); setCommentsTarget(s); }} style={reviewBtnGhost}>
                          {count > 0 ? <><StarsDisplay value={avg} size={12} /> ({count})</> : 'Ver opiniones'}
                        </button>
                      </div>
                    </div>
                  ); })}
                </div>
              )}
            </>
          )}

          {/* Modal de detalle con carrusel */}
          {modalService && (
            <div style={overlay} onClick={() => setModalService(null)}>
              <div style={{ ...panel, maxWidth: '520px' }} onClick={e => e.stopPropagation()}>
                <div style={panelHeader}>
                  <h3 style={{ color: theme.colors.primary, margin: 0 }}>{modalService.name}</h3>
                  <button onClick={() => setModalService(null)} style={closeBtn}>✕</button>
                </div>
                {modalImages.length > 0 && (
                  <div style={{ position: 'relative', textAlign: 'center', marginBottom: '1rem' }}>
                    {isVideoUrl(modalImages[imgIndex]) ? (
                      <video
                        key={modalImages[imgIndex]}
                        src={modalImages[imgIndex]}
                        controls
                        autoPlay
                        playsInline
                        style={{ maxWidth: '100%', maxHeight: '320px', objectFit: 'contain', borderRadius: '8px' }}
                      />
                    ) : (
                      <img src={modalImages[imgIndex]} alt="" style={{ maxWidth: '100%', maxHeight: '320px', objectFit: 'contain', borderRadius: '8px' }} />
                    )}
                    {modalImages.length > 1 && (
                      <>
                        <button onClick={() => setImgIndex(i => (i - 1 + modalImages.length) % modalImages.length)} style={{ ...arrowBtn, left: '8px' }}>◀</button>
                        <button onClick={() => setImgIndex(i => (i + 1) % modalImages.length)} style={{ ...arrowBtn, right: '8px' }}>▶</button>
                      </>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '1.2rem', alignItems: 'baseline', marginBottom: '0.6rem' }}>
                  <span style={{ color: theme.colors.primary, fontWeight: 700, fontSize: '1.1rem' }}>{modalService.price != null ? fmt(modalService.price) : 'Consultar'}</span>
                  <span style={{ color: theme.colors.textMuted, fontSize: '0.9rem' }}>⏱ {modalService.time} min</span>
                </div>
                <p style={{ color: theme.colors.textMuted, fontSize: '0.9rem', lineHeight: 1.6 }}>{modalService.description || 'Sin descripción.'}</p>
                <button onClick={() => startScheduling(modalService)} style={{ ...primaryBtn, width: '100%', marginTop: '1rem' }}>
                  Agendar
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ─── AGENDAMIENTO ─── */}
      {view === 'schedule' && chosenService && (
        <div style={{ maxWidth: '560px', margin: '0 auto' }}>
          <button onClick={() => { setView('catalog'); setChosenService(null); }} style={{ ...secondaryBtn, marginBottom: '1rem' }}>← Volver a servicios</button>
          <div style={{ backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.border}`, borderRadius: '14px', padding: '1.5rem' }}>
            <h2 style={{ color: theme.colors.primary, fontSize: '1.15rem', margin: '0 0 0.3rem' }}>{chosenService.name}</h2>
            <p style={{ color: theme.colors.textMuted, fontSize: '0.85rem', margin: '0 0 1.2rem' }}>
              {chosenService.price != null ? `${fmt(chosenService.price)} · ` : ''}⏱ {chosenService.time} min
            </p>

            {store.per_user_calendar && (
              <div style={field}>
                <label style={label}>Agendar cita con *</label>
                {staff.length === 0 ? (
                  <p style={muted}>No hay personas disponibles para este servicio por ahora.</p>
                ) : (
                  <select
                    value={chosenUserId}
                    onChange={e => setChosenUserId(e.target.value)}
                    style={{ ...input, cursor: 'pointer' }}
                  >
                    <option value="">Selecciona con quién agendar…</option>
                    {staff.map(u => (
                      <option key={u.id} value={String(u.id)}>{u.name}</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {(!store.per_user_calendar || chosenUserId) && (
              <div style={field}>
                <label style={label}>Elige el día *</label>
                {renderCalendar()}
              </div>
            )}

            {date && (
              <div style={field}>
                <label style={label}>Hora *</label>
                {loadingSlots ? (
                  <p style={{ color: theme.colors.textMuted, fontSize: '0.88rem' }}>Buscando horarios...</p>
                ) : !slotsData?.dayEnabled ? (
                  <p style={muted}>La empresa no atiende este día. Elige otra fecha.</p>
                ) : slotsData?.isHoliday ? (
                  <p style={muted}>Ese día es festivo, no hay atención. Elige otra fecha.</p>
                ) : slotsData?.slots?.length === 0 ? (
                  <p style={muted}>No hay horarios disponibles este día.</p>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(78px, 1fr))', gap: '0.5rem' }}>
                    {slotsData.slots.map(s => (
                      <button
                        key={s.time} type="button" disabled={!s.available}
                        onClick={() => setTime(s.time)}
                        title={s.available ? '' : 'Hora ocupada'}
                        style={{
                          padding: '9px 6px', borderRadius: '8px', fontSize: '0.9rem',
                          border: `1px solid ${time === s.time ? theme.colors.primary : theme.colors.border}`,
                          cursor: s.available ? 'pointer' : 'not-allowed',
                          backgroundColor: !s.available ? theme.colors.background : (time === s.time ? theme.colors.primary : 'transparent'),
                          color: !s.available ? theme.colors.textMuted : (time === s.time ? theme.colors.background : theme.colors.text),
                          textDecoration: s.available ? 'none' : 'line-through',
                          opacity: s.available ? 1 : 0.55,
                          fontWeight: time === s.time ? 700 : 400,
                        }}
                      >{s.time}</button>
                    ))}
                  </div>
                )}
                {slotsData?.slots?.some(s => !s.available) && (
                  <p style={{ color: theme.colors.textMuted, fontSize: '0.74rem', marginTop: '0.5rem' }}>Las horas tachadas ya están ocupadas.</p>
                )}
              </div>
            )}

            {time && (
              <>
                <div style={field}>
                  <label style={label}>Tu nombre *</label>
                  <input value={name} onChange={e => setName(e.target.value)} style={input} placeholder="Nombre completo" />
                </div>
                <div style={field}>
                  <label style={label}>Teléfono / celular *</label>
                  <input
                    value={phone}
                    onChange={e => setPhone(store.strict_phone ? onlyDigits(e.target.value) : e.target.value)}
                    style={input}
                    placeholder="Ej: 3001234567"
                    inputMode={store.strict_phone ? 'numeric' : 'tel'}
                  />
                </div>
              </>
            )}

            {errorMsg && <p style={{ color: theme.colors.error, fontSize: '0.85rem', marginBottom: '0.8rem' }}>{errorMsg}</p>}
            {(() => {
              const disabled = submitting || !time || !name.trim() || !phone.trim();
              const method = store.payment_method || 'onsite';
              const btn = (extra) => ({ ...primaryBtn, width: '100%', marginTop: '0.5rem', opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer', ...extra });
              if (method === 'both') {
                return (
                  <div style={{ marginTop: '0.5rem' }}>
                    <p style={{ color: theme.colors.text, fontWeight: 600, marginBottom: '0.6rem' }}>¿Cómo deseas pagar?</p>
                    <button onClick={() => submit('online')} disabled={disabled} style={btn()}>
                      {submitting ? 'Procesando...' : 'Pagar en línea'}
                    </button>
                    <button onClick={() => submit('onsite')} disabled={disabled} style={{ ...secondaryBtn, width: '100%', marginTop: '0.6rem', padding: '12px 24px', fontWeight: 700, fontSize: '0.95rem', opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
                      {submitting ? 'Procesando...' : 'Pagar el día de la cita'}
                    </button>
                  </div>
                );
              }
              return (
                <button onClick={() => submit(method)} disabled={disabled} style={btn()}>
                  {submitting ? 'Procesando...' : (method === 'online' ? 'Pagar y agendar' : 'Confirmar cita')}
                </button>
              );
            })()}
          </div>
        </div>
      )}

      {/* ─── MODAL: CANCELAR CITA ─── */}
      {showCancel && (
        <div style={overlay} onClick={() => setShowCancel(false)}>
          <div style={{ ...panel, maxWidth: '480px' }} onClick={e => e.stopPropagation()}>
            <div style={panelHeader}>
              <h3 style={{ color: '#f87171', margin: 0 }}>Cancelar cita</h3>
              <button onClick={() => setShowCancel(false)} style={closeBtn}>✕</button>
            </div>

            <div style={field}>
              <label style={label}>Tu número de celular *</label>
              <input
                value={cancelPhone}
                onChange={e => setCancelPhone(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') searchMyAppointments(); }}
                placeholder="Ej. 3001234567"
                inputMode="tel"
                style={input}
              />
            </div>
            <button onClick={searchMyAppointments} disabled={cancelBusy} style={{ ...primaryBtn, width: '100%', opacity: cancelBusy ? 0.6 : 1 }}>
              {cancelBusy ? 'Buscando...' : 'Buscar mis citas'}
            </button>

            {cancelledMsg && (
              <p style={{ margin: '1rem 0 0', padding: '0.7rem 0.9rem', borderRadius: '8px', backgroundColor: 'rgba(34, 197, 94, 0.12)', border: '1px solid #16a34a', color: '#86efac', fontSize: '0.9rem' }}>
                ✓ {cancelledMsg}
              </p>
            )}
            {cancelError && (
              <p style={{ margin: '1rem 0 0', padding: '0.7rem 0.9rem', borderRadius: '8px', backgroundColor: 'rgba(239, 68, 68, 0.12)', border: '1px solid #dc2626', color: '#fca5a5', fontSize: '0.9rem' }}>
                {cancelError}
              </p>
            )}

            {cancelList !== null && (
              cancelList.length === 0 ? (
                <p style={{ ...muted, marginTop: '1rem' }}>
                  No encontramos citas pendientes con ese celular de hoy en adelante.
                </p>
              ) : (
                <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {cancelList.map(a => (
                    <div key={a.id} style={{ border: '1px solid var(--pub-border, rgba(56, 189, 248, 0.3))', borderRadius: '10px', padding: '0.8rem' }}>
                      <p style={{ color: 'var(--pub-text, #ffffff)', fontSize: '0.95rem', fontWeight: 600, margin: '0 0 3px' }}>{a.service}</p>
                      <p style={{ color: 'var(--pub-muted, #94a3b8)', fontSize: '0.85rem', margin: '0 0 0.7rem' }}>
                        {prettyDate(a.date)} · {a.time} · {a.duration} min
                      </p>
                      {confirmId === a.id ? (
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{ color: 'var(--pub-muted, #94a3b8)', fontSize: '0.84rem' }}>¿Seguro?</span>
                          <button onClick={() => confirmCancel(a.id)} disabled={cancelBusy} style={{ ...cancelCitaBtn, padding: '7px 14px', fontSize: '0.84rem' }}>
                            {cancelBusy ? 'Cancelando...' : 'Sí, cancelar'}
                          </button>
                          <button onClick={() => setConfirmId(null)} style={secondaryBtn}>No</button>
                        </div>
                      ) : (
                        <button onClick={() => { setConfirmId(a.id); setCancelledMsg(''); }} style={{ ...cancelCitaBtn, padding: '7px 14px', fontSize: '0.84rem' }}>
                          Cancelar cita
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      )}

      {/* ─── MODAL: CONSULTAR CITA ─── */}
      {showLookup && (
        <div style={overlay} onClick={() => setShowLookup(false)}>
          <div style={{ ...panel, maxWidth: '480px' }} onClick={e => e.stopPropagation()}>
            <div style={panelHeader}>
              <h3 style={{ color: theme.colors.primary, margin: 0 }}>Consultar cita</h3>
              <button onClick={() => setShowLookup(false)} style={closeBtn}>✕</button>
            </div>

            <div style={field}>
              <label style={label}>Tu número de celular *</label>
              <input
                value={lookupPhone}
                onChange={e => setLookupPhone(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') searchLastAppointments(); }}
                placeholder="Ej. 3001234567"
                inputMode="tel"
                style={input}
              />
            </div>
            <button onClick={searchLastAppointments} disabled={lookupBusy} style={{ ...primaryBtn, width: '100%', opacity: lookupBusy ? 0.6 : 1 }}>
              {lookupBusy ? 'Buscando...' : 'Consultar mis últimas citas'}
            </button>

            {lookupError && (
              <p style={{ margin: '1rem 0 0', padding: '0.7rem 0.9rem', borderRadius: '8px', backgroundColor: 'rgba(239, 68, 68, 0.12)', border: '1px solid #dc2626', color: '#fca5a5', fontSize: '0.9rem' }}>
                {lookupError}
              </p>
            )}

            {lookupResult !== null && (
              lookupResult.length === 0 ? (
                <p style={{ ...muted, marginTop: '1rem' }}>No encontramos citas agendadas con ese celular.</p>
              ) : (
                <div style={{ marginTop: '1rem' }}>
                  <p style={{ color: 'var(--pub-muted, #94a3b8)', fontSize: '0.8rem', margin: '0 0 0.5rem' }}>
                    {lookupResult.length === 1 ? 'Tu última cita agendada' : `Tus últimas ${lookupResult.length} citas agendadas`}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                    {lookupResult.map(a => {
                      // Colores fijos (no el acento del tema), igual que el botón de cancelar:
                      // rojo = cancelada, amarillo = ya pasó; deben distinguirse en cualquier paleta.
                      const cancelled = a.status === 'cancelada';
                      const past = !cancelled && a.is_past;
                      const tone = cancelled
                        ? { border: '#dc2626', bg: 'rgba(239, 68, 68, 0.10)', text: '#f87171', badgeBg: '#dc2626', badgeText: '#ffffff', badge: '❌ CANCELADA' }
                        : past
                          ? { border: '#ca8a04', bg: 'rgba(234, 179, 8, 0.10)', text: '#facc15', badgeBg: '#eab308', badgeText: '#1c1917', badge: '⏰ YA PASÓ' }
                          : null;
                      return (
                        <div key={a.id} style={{ border: `1px solid ${tone ? tone.border : 'var(--pub-border, rgba(56, 189, 248, 0.3))'}`, backgroundColor: tone ? tone.bg : 'transparent', borderRadius: '10px', padding: '0.9rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '3px' }}>
                            <p style={{ color: 'var(--pub-text, #ffffff)', fontSize: '1rem', fontWeight: 600, margin: 0, textDecoration: cancelled ? 'line-through' : 'none' }}>{a.service}</p>
                            {tone && (
                              <span style={{ flex: '0 0 auto', backgroundColor: tone.badgeBg, color: tone.badgeText, fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.5px', padding: '3px 8px', borderRadius: '6px' }}>{tone.badge}</span>
                            )}
                          </div>
                          <p style={{ color: tone ? tone.text : theme.colors.primary, fontSize: '0.95rem', fontWeight: 700, margin: '0 0 3px', textDecoration: cancelled ? 'line-through' : 'none' }}>
                            {prettyDate(a.date)} · {a.time}
                          </p>
                          {a.staff_name && (
                            <p style={{ color: 'var(--pub-text, #ffffff)', fontSize: '0.9rem', margin: '0 0 3px' }}>
                              👤 Con: <strong>{a.staff_name}</strong>
                            </p>
                          )}
                          <p style={{ color: 'var(--pub-muted, #94a3b8)', fontSize: '0.85rem', margin: '0 0 0.5rem' }}>
                            ⏱ {a.duration} min · Cita #{a.id}
                          </p>
                          <p style={{ fontSize: '0.85rem', fontWeight: 700, margin: 0, color: tone ? tone.text : 'var(--pub-text, #ffffff)' }}>
                            {cancelled
                              ? 'Cita cancelada: esta cita NO se va a atender.'
                              : past
                                ? 'Esta cita ya pasó.'
                                : 'Cita vigente. ¡Te esperamos!'}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      )}

      {reviewTarget && (
        <ReviewModal
          mode="service" slug={slug} target={reviewTarget}
          onClose={() => setReviewTarget(null)}
          onSubmitted={() => setReviewRefresh(n => n + 1)}
        />
      )}
      {commentsTarget && (
        <CommentsModal
          withRating heading={`Opiniones · ${commentsTarget.name}`}
          comments={commentsTarget.comments || []}
          onClose={() => setCommentsTarget(null)}
        />
      )}
    </main>
    </div>
  );
}

const center = { padding: '4rem 1.5rem', textAlign: 'center', color: 'var(--pub-muted, #94a3b8)' };
const field = { marginBottom: '1rem' };
const label = { display: 'block', color: 'var(--pub-muted, #94a3b8)', fontSize: '0.82rem', marginBottom: '0.3rem' };
const input = { width: '100%', padding: '10px 12px', backgroundColor: 'var(--pub-bg, #0f0f0f)', border: '1px solid var(--pub-border, rgba(56, 189, 248, 0.3))', borderRadius: '8px', color: 'var(--pub-text, #ffffff)', fontSize: '0.92rem', boxSizing: 'border-box', outline: 'none' };
const muted = { color: 'var(--pub-muted, #94a3b8)', fontSize: '0.88rem', backgroundColor: 'var(--pub-bg, #0f0f0f)', border: '1px solid var(--pub-border, rgba(56, 189, 248, 0.3))', borderRadius: '8px', padding: '0.7rem 0.9rem', margin: 0 };
const primaryBtn = { padding: '12px 24px', backgroundColor: 'var(--pub-accent, #38bdf8)', color: 'var(--pub-bg, #0f0f0f)', border: 'none', borderRadius: '9px', fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem' };
const secondaryBtn = { padding: '8px 16px', background: 'none', border: '1px solid var(--pub-border, rgba(56, 189, 248, 0.3))', color: 'var(--pub-text, #ffffff)', borderRadius: '8px', cursor: 'pointer', fontSize: '0.88rem' };
// Rojo fijo, no el acento del tema: cancelar es destructivo y debe leerse igual
// en cualquier paleta que configure la empresa.
const cancelCitaBtn = { padding: '10px 20px', backgroundColor: '#dc2626', color: '#ffffff', border: 'none', borderRadius: '9px', fontWeight: 700, cursor: 'pointer', fontSize: '0.92rem' };
const reviewBtn = { flex: '1 1 auto', padding: '6px 8px', borderRadius: '6px', border: 'none', background: 'var(--pub-accent, #38bdf8)', color: 'var(--pub-bg, #0f0f0f)', cursor: 'pointer', fontSize: '0.76rem', fontWeight: 700, whiteSpace: 'nowrap' };
const reviewBtnGhost = { flex: '1 1 auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '6px 8px', borderRadius: '6px', border: '1px solid var(--pub-border, rgba(56, 189, 248, 0.3))', background: 'none', color: 'var(--pub-text, #ffffff)', cursor: 'pointer', fontSize: '0.76rem', whiteSpace: 'nowrap' };
const overlay = { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' };
const panel = { backgroundColor: 'var(--pub-surface, #1a1a2e)', borderRadius: '14px', border: '1px solid var(--pub-border, rgba(56, 189, 248, 0.3))', padding: '1.5rem', width: '100%', maxHeight: '88vh', overflow: 'auto' };
const panelHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' };
const closeBtn = { background: 'none', border: 'none', color: 'var(--pub-muted, #94a3b8)', fontSize: '1.3rem', cursor: 'pointer' };
const arrowBtn = { position: 'absolute', top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer' };

export default PublicCita;
